/**
 * Model Finder — Shared Selection-State Manager (single id).
 *
 * Persists/restores the user's selection across visits. The selection collapsed
 * to a single value — the deepest selected node id (`selectedId`) — from which
 * the whole column path re-derives via the tree's `ancestors` spine (spec §3.5).
 *
 * Storage routes through the ported versioned envelope (`lib/versioned`) +
 * an incognito-safe `StorageAccessor` (`lib/storage`):
 *   - the current shape is **v3** `{ selectedId }`, where `selectedId` is a
 *     number (legacy `alt` source) or a 22-char public id (`datte`, W0-4);
 *   - a numeric **v2** entry carries over unchanged (v2→v3 is the identity);
 *   - a returning visitor's legacy **v1** entry (the old multi-field
 *     `{ manufacturer, deviceType, … }` path) is MIGRATED to `selectedId`, not
 *     dropped, via the plugin's v1→v2 `migrationToNext`.
 *
 * **Every read is validated against `MfStateV3`.** The migration engine returns a
 * same-version entry UNCHECKED (its `fromVersion === targetVersion` short-circuit),
 * so without the final `parse` a hand-edited / corrupt v3 entry (`selectedId:
 * "../x"`) would flow straight into a fetch URL.
 *
 * **Legacy envelope adapter:** the old `ModelFinderStateManager` wrote
 * `{ version: 1, data: <MfState> }` — the field was `data`, but the ported
 * `isVersionedEnvelope` requires `value`. `adaptLegacyEnvelope` renames
 * `data` → `value` once on read so the v1 envelope is recognised before the
 * v1→v2 migration runs.
 *
 * @see src-js/lib/storage/README.md (versioned-envelope code pattern)
 * @see docs/speed-optimization/research/2026-06-06 - datte-model-finder-endpoints/spec.md §3.8
 */

import { StorageAccessor } from '../lib/storage/storage-accessor';
import {
  modelFinderStatePlugin,
  MfStateV3,
} from '../lib/storage/plugins/model-finder-state.plugin';
import { createVersionedSerializer } from '../lib/versioned/serializer';
import { type NodeId, nodeId } from './domain/brand';

const STORAGE_KEY = 'datte_mf_state';

const accessor = new StorageAccessor();
const serializer = createVersionedSerializer(modelFinderStatePlugin);

/**
 * One-time read adapter: the legacy envelope wrote the body under `data`, but
 * the versioned serializer expects `value`. Rename `{version,data}` →
 * `{version,value}` so `isVersionedEnvelope` recognises the v1 envelope. A v2/v3
 * (or already-`value`) entry passes through unchanged.
 */
function adaptLegacyEnvelope(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      'version' in parsed &&
      'data' in parsed &&
      !('value' in parsed)
    ) {
      return JSON.stringify({ version: parsed.version, value: parsed.data });
    }
  } catch {
    // not JSON — let the serializer reject it on deserialize.
  }
  return raw;
}

export class ModelFinderStateManager {
  /** Persist the deepest selected id. Silently no-ops when storage is unavailable. */
  static save(selectedId: NodeId | null): void {
    // The brand erases — the JSON layer stores a plain `number|string|null`
    // (MfStateV3), the id exactly as received (ADR-0011 §5, W0-4 spec §3.4).
    accessor.set(STORAGE_KEY, serializer.serialize({ selectedId }));
  }

  /**
   * Load the persisted `selectedId`, migrating a legacy multi-field entry up to
   * the single-id shape. Returns `null` when nothing valid is stored.
   */
  static load(): NodeId | null {
    const raw = accessor.get(STORAGE_KEY);
    if (!raw) return null;
    try {
      // Validate AFTER migration: a same-version entry is not checked by the
      // engine (see the header), so `parse` is the trust boundary here.
      const state = MfStateV3.parse(serializer.deserialize(adaptLegacyEnvelope(raw)));
      // Brand at the storage boundary: the schema already enforced the id shape.
      return state.selectedId === null ? null : nodeId(state.selectedId);
    } catch {
      // Corrupt / unmigratable — discard and behave as empty.
      this.clear();
      return null;
    }
  }

  /** Remove the persisted state. */
  static clear(): void {
    accessor.remove(STORAGE_KEY);
  }
}
